import { uiApi } from "$lib/ui-api";
import { logger } from "$lib/services/logger";
import CartErrors from "$components/cart/cart-errors.svelte";
import ErrorModal from "$components/modals/error-modal.svelte";
import PriceChange from "$components/cart/price-changed.svelte";
import { writable } from "svelte/store";
import { openModal } from "@thehood/svlib";
import type { IAddress } from "types/backend/models/address.entity";
import type { IBaseCartItem } from "types/backend/models/checkout/base-cart-item.entity";
import type { CartItemHotel } from "types/backend/models/checkout/cart-item/hotel.entity";
import type { CartItemTicket } from "types/backend/models/checkout/cart-item/ticket.entity";
import type { ICartDetails } from "types/backend/models/checkout/cart.entity";

type ExtendedCartDetails = ICartDetails & { showFees: boolean };

export interface ISessionCheckoutDetails extends ISessionCheckoutDetailsRaw {
  addressSameShip?: string;
  addresses?: IAddress[];
  useCredits?: string;
}

export interface ISessionCheckoutDetailsRaw {
  forename?: string;
  surname?: string;
  telephonePrefix?: string;
  telephone?: string;
  email?: string;
  billingLine1?: string;
  billingLine2?: string;
  billingCity?: string;
  billingPostcode?: string;
  billingCountry?: string;
  billingCountryCode?: string;
  shippingLine1?: string;
  shippingLine2?: string;
  shippingCity?: string;
  shippingPostcode?: string;
  shippingCountry?: string;
  shippingCountryCode?: string;
}

export const cart = writable<ExtendedCartDetails>({} as ExtendedCartDetails);
export const cartTimers = writable<Record<string, IExpiringItem>>({});

const URL = "/z/cart";
let localCart: ICartDetails;
let expiryTimer: NodeJS.Timeout;

// CRUD Functions
export async function cartGet() {
  try {
    for (const fn of beforeCartFunctionMap.values()) {
      await fn();
    }

    const resp = await uiApi.getRaw(URL);
    if (!resp.ok) {
      const errorText = (await resp.text()) || "Failed getting cart";
      throw new Error(errorText);
    }

    const latestCart = await resp.json();

    updateSharedCart(latestCart);

    const itemsChange = (latestCart?.items ?? [])
      .filter((v: IBaseCartItem) => v?.uiMessage)
      .map((v: IBaseCartItem) => ({ sku: v.sku, message: v?.uiMessage || "" }));

    if (itemsChange.length) {
      openModal(PriceChange, { itemsChange });
    }

    if (latestCart?.errors?.length) {
      openModal(CartErrors, { errors: latestCart.errors });
    }

    for (const fn of afterCartFunctionMap.values()) {
      await fn();
    }

    return latestCart as ExtendedCartDetails;
  } catch (err) {
    openModal(ErrorModal, { message: (err as Error).message });
  }
}

export async function cartAdd(
  productId: string,
  qty: number,
  adult?: number,
  children?: number,
  infant?: number,
  checkIn?: Date,
  checkOut?: Date
) {
  logger.silly("adding", productId);
  if (!qty) {
    throw new Error("Unable to add quantity 0 to the cart!");
  }
  try {
    for (const fn of beforeCartFunctionMap.values()) {
      await fn();
    }

    const resp = await uiApi.postRaw(URL, { productId, qty, checkIn, checkOut, adult, children, infant });
    if (!resp.ok) {
      const errorMessage = (await resp.text()) || "Unknown error occurred";
      throw new Error(errorMessage);
    }

    for (const fn of afterCartFunctionMap.values()) {
      await fn();
    }

    return await cartGet();
  } catch (err) {
    openModal(ErrorModal, { message: (err as Error).message });
  }
}

export async function clearErrors(sku?: string) {
  logger.silly("removing cart errors");
  try {
    for (const fn of beforeCartFunctionMap.values()) {
      await fn();
    }
    const resp = await uiApi.postRaw(`${URL}/errors`, { sku });
    if (!resp.ok) {
      throw new Error(await resp.text());
    }
    for (const fn of afterCartFunctionMap.values()) {
      await fn();
    }
  } catch (err) {
    openModal(ErrorModal, { message: (err as Error).message });
    await cartClear();
  }
}

export async function cartUpdateOnPriceChange(sku: string[]) {
  logger.silly("Price updating cart", sku);
  return await uiApi.postRaw(`${URL}/update-price`, { sku });
}

// export async function checkAvailability() {
//   const resp = await uiApi.postRaw(`${URL}/check`, {});

//   if (!resp.ok) {
//     throw new Error(await resp.text());
//   }

//   for (const fn of afterCartFunctionMap.values()) {
//     await fn();
//   }

//   await cartGet();
//   return await resp.json();
// }

export async function holdCart() {
  logger.silly("holding cart");
  try {
    for (const fn of beforeCartFunctionMap.values()) {
      await fn();
    }

    const resp = await uiApi.postRaw(`${URL}/hold`, {});

    if (!resp.ok) {
      throw new Error(await resp.text());
    }

    for (const fn of afterCartFunctionMap.values()) {
      await fn();
    }

    await cartGet();
    return await resp.json();
  } catch (err) {
    throw err;
  }
}

export async function cartRelease() {
  logger.silly("Release cart full cart.");
  try {
    for (const fn of beforeCartFunctionMap.values()) {
      await fn();
    }

    const resp = await uiApi.postRaw(`${URL}/release`, {});
    if (!resp.ok) {
      throw new Error(await resp.text());
    }

    for (const fn of afterCartFunctionMap.values()) {
      await fn();
    }

    await cartGet();
  } catch (err) {
    throw err;
  }
}

export async function cartRemove(sku: string, qty?: number) {
  logger.silly("removing ", sku);
  try {
    for (const fn of beforeCartFunctionMap.values()) {
      await fn();
    }
    const resp = await uiApi.deleteRaw(URL, `${sku}/${qty}`);
    if (!resp.ok) {
      throw new Error(await resp.text());
    }
    for (const fn of afterCartFunctionMap.values()) {
      await fn();
    }

    await cartGet();
  } catch (err) {
    openModal(ErrorModal, { message: (err as Error).message });
  }
}

export async function cartClear() {
  logger.silly("removing whole cart");
  try {
    for (const fn of beforeCartFunctionMap.values()) {
      await fn();
    }
    const resp = await uiApi.deleteRaw(URL, "");
    if (!resp.ok) {
      throw new Error(await resp.text());
    }
    for (const fn of afterCartFunctionMap.values()) {
      await fn();
    }
  } catch (err) {
    openModal(ErrorModal, { message: (err as Error).message });
  }
}

export async function cartApplyDiscountCode(discountCode: string) {
  try {
    for (const fn of beforeCartFunctionMap.values()) {
      await fn();
    }
    const resp = await uiApi.postRaw(`${URL}/discount-code`, { discountCode });
    for (const fn of afterCartFunctionMap.values()) {
      await fn();
    }
    if (!resp.ok) {
      throw new Error(await resp.text());
    }

    return await cartGet();
  } catch (err) {
    throw err;
  }
}

export async function cartUpdateDetails(sessionCheckOut: ISessionCheckoutDetailsRaw) {
  //only for Checkout
  const resp = await uiApi.postRaw("/checkout/api/checkout-details", sessionCheckOut);
  if (!resp.ok) {
    throw new Error("Missing stuff, unable to update");
  }

  return await cartGet();
}

// Expiry Timer functions
interface IExpiringItem {
  min: number;
  sec: number;
}

export async function cartClearMessage(sku: string) {
  // logger.silly("Internal", sessionCheckOut);
  const resp = await uiApi.postRaw(`${URL}/message`, { sku });
  if (!resp.ok) {
    throw new Error("Problem updating the cart.");
  }

  return (await cartGet()) as ExtendedCartDetails;
}

export async function cartExpireRemove() {
  logger.silly("something expired only");
  return uiApi.deleteRaw(`${URL}/expired`, "").then(cartGet);
}

// export async function cartExtend() {
//   logger.silly("extend cart");
//   const resp = await uiApi.postRaw(`${URL}/extend`);
//   if (!resp.ok) {
//     logger.error(`${resp.status} - ${await resp.text()}`);
//     await cartGet();
//   } else {
//     const data = await resp.json();
//     updateSharedCart(data);
//   }

//   return resp.ok;
// }

function updateSharedCart(value: ExtendedCartDetails) {
  clearInterval(expiryTimer);
  cart.set(value);
  localCart = value;
  runExpiryTimer();
  return value;
}

function runExpiryTimer() {
  setExpiryTimer();
  expiryTimer = setInterval(setExpiryTimer, 1000);
}

function setExpiryTimer() {
  const expiryList: Record<string, IExpiringItem> = {};
  (localCart?.items || []).map(v => {
    const { sku, expiry } = v;
    if (expiry) {
      const timeRemaining = new Date(expiry).getTime() - Date.now();
      const minutes = Math.max(~~((timeRemaining % (1000 * 60 * 60)) / (1000 * 60)), 0);
      const seconds = Math.max(~~((timeRemaining % (1000 * 60)) / 1000), 0);
      expiryList[`${sku}`] = {
        min: minutes,
        sec: seconds,
      };
    }
  });
  cartTimers.set(expiryList);
}

// Hooks
type IPromiseFn = () => Promise<void>;
const beforeCartFunctionMap = new Map<string, IPromiseFn>();
const afterCartFunctionMap = new Map<string, IPromiseFn>();

export function beforeCartGetFnSet(name: string, fn: IPromiseFn) {
  beforeCartFunctionMap.set(name, fn);
}

export function beforeCartAddFnSet(name: string, fn: IPromiseFn) {
  beforeCartFunctionMap.set(name, fn);
}

export function beforeCartRemoveFnSet(name: string, fn: IPromiseFn) {
  beforeCartFunctionMap.set(name, fn);
}

export function beforeCartClearFnSet(name: string, fn: IPromiseFn) {
  beforeCartFunctionMap.set(name, fn);
}

export function afterCartGetFnSet(name: string, fn: IPromiseFn) {
  afterCartFunctionMap.set(name, fn);
}

export function afterCartAddFnSet(name: string, fn: IPromiseFn) {
  afterCartFunctionMap.set(name, fn);
}

export function afterCartRemoveFnSet(name: string, fn: IPromiseFn) {
  afterCartFunctionMap.set(name, fn);
}

export function afterCartClearFnSet(name: string, fn: IPromiseFn) {
  afterCartFunctionMap.set(name, fn);
}

export function beforeCartGetFnDel(name: string) {
  beforeCartFunctionMap.delete(name);
}

export function beforeCartAddFnDel(name: string) {
  beforeCartFunctionMap.delete(name);
}

export function beforeCartRemoveFnDel(name: string) {
  beforeCartFunctionMap.delete(name);
}

export function beforeCartClearFnDel(name: string) {
  beforeCartFunctionMap.delete(name);
}

export function afterCartGetFnDel(name: string) {
  afterCartFunctionMap.delete(name);
}

export function afterCartAddFnDel(name: string) {
  afterCartFunctionMap.delete(name);
}

export function afterCartRemoveFnDel(name: string) {
  afterCartFunctionMap.delete(name);
}

export function afterCartClearFnDel(name: string) {
  afterCartFunctionMap.delete(name);
}

/**Typeguard*/
export function itemIsTicket(item: IBaseCartItem): item is CartItemTicket {
  return item.productType === "ticket";
}

export function itemIsHotel(item: IBaseCartItem): item is CartItemHotel {
  return item.productType === "hotel";
}
